import React, { useState, useMemo } from 'react';
import { Tabs, theme, Result } from 'antd';
import { 
    AppstoreOutlined,
    TagsOutlined
} from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { hasPermission, shopifyCategoryPermissions } from '../../utils/permissions';

import ShopifyCategorySettings from '../shopify/ShopifyCategories/components/ShopifyCategorySettings';
import ShopifyCollectionSettings from '../shopify/ShopifyCategories/components/ShopifyCollectionSettings';
import './ShopifyCategoryManagement.css';

const { TabPane } = Tabs;

const ShopifyCategoryManagement = () => {
    const [activeKey, setActiveKey] = useState('shopify-categories');
    const { user } = useSelector(state => state.auth);
    const {
        token: { colorBgContainer, borderRadiusLG },
    } = theme.useToken();

    // Check permissions
    const canViewCategories = shopifyCategoryPermissions.canRead(user);
    const canManageShopifyCategories = shopifyCategoryPermissions.canManage(user);

    // Define all possible tabs with their permission requirements
    const allTabs = [
        {
            key: 'shopify-categories',
            tab: <span><AppstoreOutlined /> Danh mục Shopify</span>,
            component: <ShopifyCategorySettings user={user} />,
            permission: canManageShopifyCategories
        },
        {
            key: 'shopify-collections',
            tab: <span><TagsOutlined /> Collections</span>,
            component: <ShopifyCollectionSettings user={user} />,
            permission: canManageShopifyCategories
        }
    ];

    // Filter tabs based on permissions
    const availableTabs = useMemo(() => {
        return allTabs.filter(tab => tab.permission);
    }, [canManageShopifyCategories]);

    // Set default active key to first available tab
    const defaultActiveKey = useMemo(() => {
        if (availableTabs.length > 0) {
            if (!availableTabs.find(tab => tab.key === activeKey)) {
                return availableTabs[0].key;
            }
        }
        return activeKey;
    }, [availableTabs, activeKey]);

    // Update active key if it changed due to permission filtering
    React.useEffect(() => {
        if (defaultActiveKey !== activeKey) {
            setActiveKey(defaultActiveKey);
        }
    }, [defaultActiveKey, activeKey]);

    // If user can't view any categories, show access denied
    if (!canViewCategories) {
        return (
            <div style={{ margin: '24px 16px' }}>
                <Result
                    status="403"
                    title="403"
                    subTitle="Xin lỗi, bạn không có quyền truy cập trang này."
                />
            </div>
        );
    }

    // If no tabs available after permission check, show no access
    if (availableTabs.length === 0) {
        return (
            <div style={{ margin: '24px 16px' }}>
                <Result
                    status="403"
                    title="Không có quyền truy cập"
                    subTitle="Bạn không có quyền quản lý bất kỳ danh mục Shopify nào."
                />
            </div>
        );
    }

    return (
        <div
            style={{
                margin: '24px 16px',
                borderRadius: borderRadiusLG,
                minHeight: 280,
            }}
        >
            <Tabs
                activeKey={activeKey}
                onChange={setActiveKey}
                type="card"
                tabBarGutter={5}
                className="category-management-tabs"
            >
                {availableTabs.map(tab => (
                    <TabPane tab={tab.tab} key={tab.key}>
                        {tab.component}
                    </TabPane>
                ))}
            </Tabs>
        </div>
    );
};

export default ShopifyCategoryManagement;
